import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ApiError } from '../../api/index.ts';
import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { DEVICE_COUNT_FIELDS, DEVICE_COUNT_LABELS, validateDeviceCountField } from './deviceCountHelpers.ts';
import {
  buildShopUpdatePayload,
  initialShopEditForm,
  validateCountryCode,
  validateCurrencyCode,
  validateShopEditForm,
  type ShopEditFormState,
} from './shopEditHelpers.ts';
import { SHOP_TYPE_OPTIONS } from './shopsHelpers.ts';

export type ShopEditGroup = 'profile' | 'contact' | 'devices';

const GROUP_TITLES: Record<ShopEditGroup, string> = {
  profile: 'Edit profile',
  contact: 'Edit contact',
  devices: 'Edit devices',
};

export interface ShopEditDialogProps {
  open: boolean;
  shop: ShopSummary;
  group: ShopEditGroup;
  onClose: () => void;
  onSaved: () => void;
}

export function ShopEditDialog({
  open,
  shop,
  group,
  onClose,
  onSaved,
}: Readonly<ShopEditDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const fullScreen = useFullScreenDialog();

  const [form, setForm] = useState<ShopEditFormState>(() => initialShopEditForm(shop));
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(initialShopEditForm(shop));
      setError('');
      setNameError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open, shop]);

  const set = <K extends keyof ShopEditFormState>(key: K, value: ShopEditFormState[K]) => {
    setError('');
    setNameError('');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const problem = validateShopEditForm(form);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    setNameError('');
    try {
      const patch = buildShopUpdatePayload(form, shop);
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      await api.shops.update(shop.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'shop_name_taken') {
        setNameError(e.message);
      } else {
        setError(actionError(e));
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>{GROUP_TITLES[group]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {group === 'profile' && (
            <>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Shop code (cannot be changed)
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{shop.shop_code}</Typography>
              </Box>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                fullWidth
                disabled={busy}
                error={nameError !== ''}
                helperText={nameError || undefined}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-name' } }}
              />
              <FormControl fullWidth disabled={busy}>
                <InputLabel id="shop-edit-type-label">Type</InputLabel>
                <Select
                  labelId="shop-edit-type-label"
                  label="Type"
                  value={form.shop_type}
                  onChange={(e) => set('shop_type', e.target.value)}
                  inputProps={{ 'aria-label': 'shop-edit-type' }}
                >
                  {SHOP_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Country code"
                value={form.country_code}
                onChange={(e) => set('country_code', e.target.value.toUpperCase())}
                fullWidth
                disabled={busy}
                error={validateCountryCode(form.country_code) !== null}
                helperText={validateCountryCode(form.country_code) ?? 'Usually PNG. Leave blank to keep the current value.'}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-country-code' } }}
              />
              <TextField
                label="Currency code"
                value={form.currency_code}
                onChange={(e) => set('currency_code', e.target.value.toUpperCase())}
                fullWidth
                disabled={busy}
                error={validateCurrencyCode(form.currency_code) !== null}
                helperText={validateCurrencyCode(form.currency_code) ?? 'Usually PGK. Leave blank to keep the current value.'}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-currency-code' } }}
              />
            </>
          )}
          {group === 'contact' && (
            <>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                fullWidth
                disabled={busy}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-email' } }}
              />
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                fullWidth
                disabled={busy}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-phone' } }}
              />
              <TextField
                label="Address"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                fullWidth
                disabled={busy}
                multiline
                minRows={2}
                slotProps={{ htmlInput: { 'aria-label': 'shop-edit-address' } }}
              />
            </>
          )}
          {group === 'devices' &&
            DEVICE_COUNT_FIELDS.map((field) => (
              <TextField
                key={field}
                label={DEVICE_COUNT_LABELS[field]}
                value={form[field]}
                onChange={(e) => set(field, e.target.value)}
                fullWidth
                disabled={busy}
                error={validateDeviceCountField(field, form[field]) !== null}
                helperText={validateDeviceCountField(field, form[field]) ?? '0-1000.'}
                slotProps={{ htmlInput: { 'aria-label': `shop-edit-${field}`, inputMode: 'numeric' } }}
              />
            ))}
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShopEditDialog;
